<template>
  <div class="public-lead-page" :style="pageStyle">
    <div v-if="loading" class="public-lead-state">
      <div class="public-lead-state__card"><div class="public-lead-spinner" /><p>{{ t('publicAgendamentoForm.loading') }}</p></div>
    </div>
    <div v-else-if="loadError" class="public-lead-state">
      <div class="public-lead-state__card"><h1>{{ t('publicAgendamentoForm.unavailableTitle') }}</h1><p>{{ loadError }}</p></div>
    </div>
    <div v-else-if="submitted" class="public-lead-state">
      <div class="public-lead-layout public-lead-layout--success">
        <div class="public-lead-form-area public-lead-form-area--center">
          <div class="public-lead-success-icon"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg></div>
          <h2 class="public-lead-form-area__heading">{{ t('publicAgendamentoForm.successTitle') }}</h2>
          <p class="public-lead-success-message">{{ successMessage }}</p>
        </div>
      </div>
    </div>
    <div v-else class="public-lead-page__shell">
      <div class="public-lead-layout">
        <aside class="public-lead-brand">
          <div class="public-lead-brand__bg" />
          <div class="public-lead-brand__content">
            <div class="public-lead-brand__hero">
              <div class="public-lead-brand__avatar-wrap">
                <img v-if="workspacePhotoUrl" :src="workspacePhotoUrl" :alt="workspaceName" class="public-lead-brand__avatar" />
                <span v-else class="public-lead-brand__avatar-fallback">{{ workspaceInitials }}</span>
              </div>
              <p v-if="workspaceName" class="public-lead-brand__workspace"><span class="public-lead-brand__workspace-badge">{{ workspaceName }}</span></p>
            </div>
            <div class="public-lead-brand__body">
              <h1 class="public-lead-brand__title">{{ formName }}</h1>
              <p class="public-lead-brand__tagline">{{ t('publicAgendamentoForm.tagline') }}</p>
            </div>
          </div>
        </aside>
        <div class="public-lead-form-area">
          <h2 class="public-lead-form-area__heading">{{ t('publicAgendamentoForm.fillTitle') }}</h2>
          <form class="public-lead-fields" novalidate @submit.prevent="submit">
            <div v-for="field in visibleFields" :key="field.key" class="public-lead-field">
              <label class="public-lead-field__label" :for="`paf-${field.key}`">
                {{ field.label }}<span v-if="field.required" class="public-lead-field__required">*</span>
              </label>
              <div class="public-lead-input-wrap" :class="{ 'public-lead-input-wrap--error': errors[field.key] }">
                <span class="public-lead-input-wrap__icon" v-html="field.iconSvg" />
                <input
                  :id="`paf-${field.key}`"
                  v-model="form[field.key]"
                  :type="field.inputType"
                  :inputmode="field.inputMode"
                  :autocomplete="field.autocomplete"
                  :placeholder="field.placeholder"
                  @blur="touch(field.key)"
                  @input="onFieldInput(field.key)"
                />
              </div>
              <p v-if="errors[field.key]" class="public-lead-field__error">{{ errorMessage(errors[field.key]) }}</p>
            </div>

            <div class="public-lead-field public-lead-schedule-block">
              <span class="public-lead-field__label">{{ t('publicAgendamentoForm.scheduleLabel') }}<span class="public-lead-field__required">*</span></span>
              <div
                class="public-lead-schedule"
                :class="{ 'public-lead-schedule--error': errors.schedule }"
              >
                <button type="button" class="public-lead-schedule__btn" @click="scheduleModalOpen = true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {{ scheduleSummary ? t('publicAgendamentoForm.scheduleChange') : t('publicAgendamentoForm.scheduleButton') }}
                </button>
                <p v-if="scheduleSummary" class="public-lead-schedule__summary">{{ scheduleSummary }}</p>
              </div>
              <p v-if="errors.schedule" class="public-lead-field__error">{{ errorMessage(errors.schedule) }}</p>
            </div>

            <p v-if="submitError" class="public-lead-alert public-lead-alert--error">{{ submitError }}</p>
            <button type="submit" class="public-lead-submit" :disabled="submitting">{{ submitting ? t('publicAgendamentoForm.submitting') : t('publicAgendamentoForm.submit') }}</button>
          </form>
        </div>
      </div>
    </div>
    <footer class="public-lead-footer">{{ t('publicAgendamentoForm.poweredBy') }} <a href="/" target="_blank" rel="noopener">BackLog</a></footer>

    <PublicAgendamentoScheduleModal
      :open="scheduleModalOpen"
      :token="token"
      :schedule-config="scheduleConfig"
      :initial-date="form.dataAgendamento"
      :initial-time="form.horario"
      :accent-color="workspaceColor"
      @close="scheduleModalOpen = false"
      @confirm="onScheduleConfirm"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import publicApi from '@/services/publicApi'
import { resolveMediaUrl } from '@/config/app'
import PublicAgendamentoScheduleModal from '@/components/public/PublicAgendamentoScheduleModal.vue'
import {
  validateEmail,
  validatePhone,
  validateRequiredText,
  validateCpfCnpj,
  validateCnpj,
  formatPhoneBr,
  formatCpfCnpj
} from '@/utils/publicFormValidation'
import '@/assets/styles/public-lead-form.css'

const { t, locale } = useI18n()
const route = useRoute()
const token = route.params.token

const ICONS = {
  nome: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>',
  cpf: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
  empresa: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>',
  cargo: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8z"/></svg>',
  whatsapp: '<svg fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>',
  email: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>',
  cnpj: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>'
}

const FIELD_META = {
  nome: { inputType: 'text', inputMode: 'text', autocomplete: 'name' },
  cpf: { inputType: 'text', inputMode: 'numeric', autocomplete: 'off' },
  empresa: { inputType: 'text', inputMode: 'text', autocomplete: 'organization' },
  cargo: { inputType: 'text', inputMode: 'text', autocomplete: 'organization-title' },
  whatsapp: { inputType: 'tel', inputMode: 'tel', autocomplete: 'tel' },
  email: { inputType: 'email', inputMode: 'email', autocomplete: 'email' },
  cnpj: { inputType: 'text', inputMode: 'numeric', autocomplete: 'off' }
}

const SERVICOS_FIELD_ORDER = ['nome', 'cpf', 'whatsapp', 'email']
const CRM_FIELD_ORDER = ['nome', 'empresa', 'cargo', 'whatsapp', 'email', 'cnpj']

const loading = ref(true)
const loadError = ref('')
const fieldSchema = ref('servicos')
const formName = ref('')
const fields = ref({})
const workspaceName = ref('')
const workspacePhoto = ref('')
const workspaceColor = ref('#5865F2')
const successMessage = ref('')
const submitted = ref(false)
const submitting = ref(false)
const submitError = ref('')
const scheduleModalOpen = ref(false)
const scheduleConfig = ref(null)
const touched = reactive({})
const errors = reactive({ schedule: null })

const form = ref({
  nome: '',
  cpf: '',
  empresa: '',
  cargo: '',
  whatsapp: '',
  email: '',
  cnpj: '',
  dataAgendamento: '',
  horario: ''
})

const isCrmSchema = computed(() => fieldSchema.value === 'crm')
const contactFieldOrder = computed(() => (isCrmSchema.value ? CRM_FIELD_ORDER : SERVICOS_FIELD_ORDER))

const pageStyle = computed(() => ({ '--plf-accent': workspaceColor.value || '#5865F2' }))
const workspacePhotoUrl = computed(() => workspacePhoto.value ? (resolveMediaUrl(workspacePhoto.value) || workspacePhoto.value) : '')
const workspaceInitials = computed(() => (workspaceName.value || formName.value || 'B').slice(0, 2).toUpperCase())

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const visibleFields = computed(() =>
  contactFieldOrder.value
    .filter(key => fields.value?.[key]?.enabled)
    .map(key => ({
      key,
      label: isCrmSchema.value
        ? t(`publicForm.fields.${key}`)
        : t(`publicAgendamentoForm.fields.${key}`),
      required: !fields.value[key]?.optional,
      iconSvg: ICONS[key],
      placeholder: isCrmSchema.value
        ? t(`publicForm.placeholders.${key}`)
        : t(`publicAgendamentoForm.placeholders.${key}`),
      ...FIELD_META[key]
    }))
)

const scheduleSummary = computed(() => {
  if (!form.value.dataAgendamento || !form.value.horario) return ''
  const [y, m, d] = form.value.dataAgendamento.split('-').map(Number)
  if (!y || !m || !d) return ''
  const date = new Date(y, m - 1, d)
  const dateStr = date.toLocaleDateString(dateLocale.value, { day: '2-digit', month: '2-digit', year: 'numeric' })
  return t('publicAgendamentoForm.scheduleSummary', { date: dateStr, time: form.value.horario })
})

function isEnabled (key) { return fields.value?.[key]?.enabled }

function touch (key) {
  if (key === 'schedule') {
    touched.schedule = true
    validateSchedule()
    return
  }
  touched[key] = true
  validateField(key)
}

function validateSchedule () {
  if (!form.value.dataAgendamento || !form.value.horario) {
    errors.schedule = 'required'
    return 'required'
  }
  errors.schedule = null
  return null
}

function validateField (key) {
  if (!isEnabled(key)) {
    errors[key] = null
    return null
  }
  const optional = !!fields.value[key]?.optional
  const required = !optional
  let err = null
  const val = form.value[key]
  switch (key) {
    case 'nome':
      err = validateRequiredText(val, true)
      break
    case 'empresa':
    case 'cargo':
      err = validateRequiredText(val, required)
      break
    case 'cpf':
      err = validateCpfCnpj(val, required)
      break
    case 'cnpj':
      err = validateCnpj(val, required)
      break
    case 'whatsapp':
      err = validatePhone(val, required)
      break
    case 'email':
      err = validateEmail(val, required)
      break
    default:
      break
  }
  errors[key] = err
  return err
}

function validateAll () {
  let ok = true
  for (const f of visibleFields.value) {
    touched[f.key] = true
    if (validateField(f.key)) ok = false
  }
  const hasEmail = isEnabled('email')
  const hasWhats = isEnabled('whatsapp')
  if (hasEmail || hasWhats) {
    const emailVal = (form.value.email || '').trim()
    const whatsVal = (form.value.whatsapp || '').trim()
    if (!emailVal && !whatsVal) {
      if (hasEmail) {
        errors.email = 'contact'
        touched.email = true
      }
      if (hasWhats) {
        errors.whatsapp = 'contact'
        touched.whatsapp = true
      }
      ok = false
    }
  }
  touched.schedule = true
  if (validateSchedule()) ok = false
  return ok
}

function errorMessage (code) {
  if (!code) return ''
  const map = {
    required: 'validationRequired',
    invalid: 'validationInvalid',
    contact: 'validationContact',
    cpfCnpj: 'validationCpfCnpjInvalid'
  }
  return t(`publicAgendamentoForm.${map[code] || 'validationInvalid'}`)
}

function onFieldInput (key) {
  if (key === 'whatsapp') form.value.whatsapp = formatPhoneBr(form.value.whatsapp)
  if (key === 'cpf') form.value.cpf = formatCpfCnpj(form.value.cpf)
  if (key === 'cnpj') form.value.cnpj = formatCpfCnpj(form.value.cnpj)
  if (touched[key]) validateField(key)
}

function onScheduleConfirm ({ dataAgendamento, horario }) {
  form.value.dataAgendamento = dataAgendamento
  form.value.horario = horario
  touched.schedule = true
  validateSchedule()
}

async function load () {
  loading.value = true
  try {
    const { data } = await publicApi.get(`/public/agendamento-forms/${token}`)
    fieldSchema.value = data.fieldSchema || 'servicos'
    formName.value = data.formName || t('publicAgendamentoForm.defaultTitle')
    fields.value = data.fields || {}
    scheduleConfig.value = data.scheduleConfig || null
    successMessage.value = data.successMessage || t('publicAgendamentoForm.defaultSuccess')
    workspaceName.value = data.workspaceName || ''
    workspacePhoto.value = data.workspacePhoto || ''
    workspaceColor.value = data.workspaceColor || '#5865F2'
  } catch (e) {
    loadError.value = e.response?.data?.error || t('publicAgendamentoForm.unavailable')
  } finally {
    loading.value = false
  }
}

async function submit () {
  submitError.value = ''
  if (!validateAll()) return
  submitting.value = true
  try {
    const payload = {
      nome: form.value.nome,
      dataAgendamento: form.value.dataAgendamento,
      horario: form.value.horario
    }
    if (isEnabled('whatsapp')) payload.whatsapp = formatPhoneBr(form.value.whatsapp)
    if (isEnabled('email')) payload.email = form.value.email
    if (isCrmSchema.value) {
      if (isEnabled('empresa')) payload.empresa = form.value.empresa
      if (isEnabled('cargo')) payload.cargo = form.value.cargo
      if (isEnabled('cnpj')) payload.cnpj = form.value.cnpj
    } else if (isEnabled('cpf')) {
      payload.cpf = form.value.cpf
    }
    const { data } = await publicApi.post(`/public/agendamento-forms/${token}/submit`, payload)
    successMessage.value = data.message || successMessage.value
    submitted.value = true
  } catch (e) {
    submitError.value = e.response?.status === 429 ? t('publicAgendamentoForm.rateLimit') : (e.response?.data?.error || t('publicAgendamentoForm.submitError'))
  } finally {
    submitting.value = false
  }
}

onMounted(load)
</script>
